import { type InputProps, useFieldState } from 'informed';
import type React from 'react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import type { WithTestId } from '@/helpers/testIds';
import type { Validator } from '@/helpers/validators';
import { useToggle } from '@/hooks/common';
import type { RegistrationConfig } from '@/modules/registration/helpers/types';
import { IconComponent } from '../IconComponent';
import { Input } from './Input';
import { PasswordStrengthChecker } from './PasswordStrengthChecker';

interface PasswordProps extends WithTestId {
  name: string;
  validate?: Validator<any, any>;
  error?: React.ReactNode;
  validateOn?:
    | 'change'
    | 'blur'
    | 'change-blur'
    | 'change-submit'
    | 'blur-submit'
    | 'submit';
  strengthChecker?: boolean;
  styleScheme?: RegistrationConfig['styleScheme'];
  withInsideLabel?: boolean;
  autoComplete?: InputProps['autoComplete'];
  touchOnChange?: boolean;
}

export const Password: FC<PasswordProps> = ({
  strengthChecker,
  withInsideLabel,
  ...props
}) => {
  const { t } = useTranslation('common');
  const password = useFieldState<string | undefined>(props.name);

  const [showPassword, toggleShowPassword] = useToggle(false);

  return (
    <ScPassword>
      <ScPasswordInputContainer>
        <Input
          addons={
            <ScButtonEye
              active={showPassword}
              aria-label="show password"
              onClick={toggleShowPassword}
              styleScheme={props.styleScheme}
              type="button"
            >
              {showPassword ? (
                <IconComponent
                  className="eye-icon"
                  name="eyeOpenIcon"
                  size="21px"
                />
              ) : (
                <IconComponent
                  className="eye-icon"
                  name="eyeClosedIcon"
                  size="21px"
                />
              )}
            </ScButtonEye>
          }
          autoComplete={props.autoComplete || props.name}
          error={props.error}
          internalLabel={
            withInsideLabel ? t('auth.registration.form.password') : ''
          }
          placeholder={
            withInsideLabel ? '' : t('auth.registration.form.password')
          }
          type={showPassword ? 'text' : 'password'}
          withInsideLabel={withInsideLabel}
          {...props}
        />
        {password.value?.length > 0 && strengthChecker && password.dirty && (
          <PasswordStrengthChecker name={props.name} />
        )}
      </ScPasswordInputContainer>
    </ScPassword>
  );
};

const ScPassword = styled.div`
  position: relative;
`;

const ScPasswordInputContainer = styled.div`
  position: relative;

  input {
    padding: 8px 48px 8px 16px;
  }
`;

export const ScButtonEye = styled.button<{
  active?: boolean;
  styleScheme?: RegistrationConfig['styleScheme'];
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 15px;
  border: none;
  position: absolute;
  right: 16px;
  top: 20px;
  transform: translateY(-50%);
  cursor: pointer;
  background: none;

  .eye-icon {
    color: ${({ theme }) => theme.color2}
  }

  ${({ styleScheme, active }) =>
    styleScheme?.input &&
    active &&
    css`
      & .eye-icon {
        color: ${styleScheme.input.colorInput};
      }
    `}

  &:hover {
    & .eye-icon {
      color: black;

      ${({ styleScheme }) =>
        styleScheme?.input &&
        css`
          color: ${styleScheme.input.colorInput};
        `}
    }
  }
`;
